"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useSyncExternalStore } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

import figure from "@/public/404/figure.png";

/**
 * 404 hero — Figma "WCE | New UI" 3941-6263 (State 2), ported 1:1 from the
 * platform (wce-platform-2026 `app/not-found.tsx`). A light hero: a huge faded
 * number seated on the bottom edge, the cut-out figure over it, and a copy block
 * with a light-blue "Back to World Class Edge" CTA.
 *
 * The animation is rebuilt in code (the shared .mov was a screen recording, not a
 * clean asset): each digit is an odometer reel that rolls into place on load — the
 * outer digits in from the bottom, the middle one in from the top — passing
 * through 141 → 232 → 323 → 414 → 404.
 *
 * NOTE ON UNITS: this repo is 8px-rem-based (`:root { font-size: calc(800vw/1920) }`)
 * while the platform is 16px-based, so every rem here is DOUBLE the platform's
 * value. `svh` / `vw` / `ch` / `cap` / `em` / `%` are rem-independent and carry
 * over unchanged. The `lg` breakpoint is 993px here (1024px on the platform).
 *
 * The repeated `calc(100svh-7rem)` is the viewport minus the height
 * <HomeNavbar/> takes in flow above this section, so the hero fills exactly the
 * rest of the screen. It is spelled out literally everywhere rather than kept in
 * a constant because Tailwind scans source text — an interpolated class name
 * never reaches the generated CSS.
 */
const EASE = "easeOut" as const;

// The entrance/roll animation runs on desktop only — mobile lands straight on the
// final state. framer can't be gated by CSS breakpoints, so we read the viewport
// (SSR-safe via useSyncExternalStore) and remount the animated elements with a
// `key` when desktop is detected, which makes their `initial` state re-apply and
// the animation play.
const DESKTOP_MQ = "(min-width: 993px)";
function subscribeDesktop(onChange: () => void) {
  const mq = window.matchMedia(DESKTOP_MQ);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}
function useIsDesktop() {
  return useSyncExternalStore(
    subscribeDesktop,
    () => window.matchMedia(DESKTOP_MQ).matches,
    () => false, // server + first hydration render: treat as mobile (static)
  );
}

// Font-size as a fraction of the section height. Desktop fills the height (÷ the
// ~0.67 cap ratio, Figma spec: leading-trim cap); mobile is a much smaller
// upper-area watermark per the Figma mobile frame (cap ≈ 28% of the height).
const NUMBER_SIZE = "text-[calc((100svh-7rem)*0.42)] lg:text-[calc((100svh-7rem)/0.78)]";
// Reel geometry, DECOUPLED so the round `0` (which overshoots the cap line, top
// and bottom) shows whole while the flat-topped `4` never reveals its neighbour:
//   WINDOW = the visible mask height — tall enough for the 0's overshoot.
//   PITCH  = the digit-to-digit spacing in the strip — a touch TALLER than the
//            window, so the adjacent digit always sits fully outside the mask (no
//            rest-time peek); the difference is the odometer band seen mid-roll.
//   SEAT   = drops each glyph's baseline onto the cell bottom (feet flush) — the
//            seated cell's bottom aligns with the window bottom. Tune by eye.
const WINDOW = "h-[1.1cap]";
const PITCH = "h-[1.22cap]";
const SEAT = "translate-y-[0.27em]";

type Reel = { seq: number[]; dir: "up" | "down" };

// Per-digit sequences, read top-to-bottom across the target values
// 141 → 232 → 323 → 414 → 404: hundreds 1‑2‑3‑4‑4, tens 4‑3‑2‑1‑0, units 1‑2‑3‑4‑4.
const SLOTS: Reel[] = [
  { seq: [1, 2, 3, 4, 4], dir: "up" },
  { seq: [4, 3, 2, 1, 0], dir: "down" },
  { seq: [1, 2, 3, 4, 4], dir: "up" },
];

/**
 * One odometer digit. Stacks the sequence and slides the reel through it with a
 * single eased tween: `up` pulls each next digit in from the bottom, `down` from
 * the top. Ends showing the last digit of `seq`.
 */
function RollingDigit({ seq, dir, animated }: Reel & { animated: boolean }) {
  // Strip is bottom-anchored: at translateY 0 its last cell sits in the window.
  // `up` starts with the first digit at the bottom and rolls up to the last;
  // `down` reverses the strip and rolls the opposite way, still ending on the
  // target (seq's last value). travel = the (N-1)/N of the strip height that moves
  // one end to the other. `%` is relative to the strip's own height, so it stays
  // correct whatever PITCH is.
  const strip = dir === "up" ? seq : [...seq].reverse();
  const travel = ((strip.length - 1) / strip.length) * 100;
  const from = dir === "up" ? `${travel}%` : "0%";
  const to = dir === "up" ? "0%" : `${travel}%`;
  return (
    <span className={`relative block ${WINDOW} w-[0.92ch] shrink-0 overflow-y-clip`}>
      <motion.span
        className="flex absolute inset-x-0 bottom-0 flex-col"
        initial={{ y: animated ? from : to }}
        animate={{ y: to }}
        transition={{ duration: 2.6, ease: [0.5, 0, 0.2, 1] }}>
        {strip.map((d, i) => (
          <span
            key={i}
            className={`flex ${PITCH} items-end justify-center leading-none`}>
            <span className={`block ${SEAT}`}>{d}</span>
          </span>
        ))}
      </motion.span>
    </span>
  );
}

export default function NotFoundHero() {
  const router = useRouter();
  const animated = useIsDesktop();
  // Remount key: flips when desktop is detected post-hydration so framer replays.
  // Each animated element needs its OWN key — three siblings sharing one string is
  // a duplicate-key error, and React may then drop or merge them.
  const animKey = animated ? "desktop" : "mobile";

  const goBack = () => (window.history.length > 1 ? router.back() : router.push("/"));

  return (
    // The navbar's black-on-light flip is driven by `data-navbar-theme="light"`
    // on the <main> wrapper, not here: <HomeNavbar/> samples the element under
    // its own vertical center, which sits ABOVE this section's top edge.
    <section
      className="overflow-hidden relative flex justify-center items-center w-full text-black bg-[#fdfdfd] px-[3rem] min-h-[calc(100svh-7rem)]">
      <h1 className="sr-only">Page not found (404)</h1>

      {/* Giant number, anchored to the bottom edge — three odometer reels roll
          into place (outer digits from the bottom, middle from the top). */}
      <motion.div
        key={`number-${animKey}`}
        aria-hidden
        initial={{ opacity: animated ? 0 : 0.06 }}
        animate={{ opacity: 0.06 }}
        transition={{ duration: 0.6, ease: EASE }}
        className={`flex absolute inset-x-0 bottom-[59%] z-0 justify-center font-normal leading-none text-black pointer-events-none select-none font-news [font-feature-settings:'lnum'_1,'tnum'_1] [font-variant-numeric:lining-nums_tabular-nums] lg:bottom-0 lg:-translate-x-[3vw] ${NUMBER_SIZE}`}>
        {SLOTS.map((s, i) => (
          <RollingDigit
            key={i}
            seq={s.seq}
            dir={s.dir}
            animated={animated}
          />
        ))}
      </motion.div>

      {/* Figure — a transparent cut-out, so the watermark reads behind it. The
          wrapper holds the responsive horizontal offset in CSS (centered on
          mobile, nudged left on desktop) so framer's transform on the inner
          element doesn't override it. */}
      <div
        className="relative z-10 aspect-[1600/2284] h-[62svh] -translate-x-[3vw] -translate-y-[14svh] lg:h-[calc(100svh-7rem)] lg:-translate-x-[5vw] lg:translate-y-0">
        <motion.div
          key={`figure-${animKey}`}
          initial={{ opacity: animated ? 0 : 1, y: animated ? 28 : 0, scale: animated ? 1.02 : 1 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, ease: EASE, delay: 0.05 }}
          className="relative w-full h-full">
          <Image
            src={figure}
            alt=""
            fill
            priority
            quality={90}
            sizes="(max-width: 992px) 60vh, 90vh"
            className="object-contain object-bottom"
          />
        </motion.div>
        {/* Fade the figure's lower body into the page so it reads as emerging from
            behind the 404 (mobile — desktop seats the figure on the bottom edge). */}
        <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-[#fdfdfd] to-transparent pointer-events-none lg:hidden" />
      </div>

      {/* Copy + CTAs — lower-right on desktop, stacked + centered on mobile. */}
      <motion.div
        key={`copy-${animKey}`}
        initial={{ opacity: animated ? 0 : 1, y: 18 }}
        animate={{ opacity: 1, y: 18 }}
        // Desktop: hold the copy hidden until the 2.6s number roll finishes, then
        // fade it in. Mobile (animated=false) shows it immediately.
        transition={{ duration: 0.6, ease: EASE, delay: 2.6 }}
        className="flex absolute inset-x-[3rem] bottom-[13%] z-20 flex-col items-center text-center gap-[3rem] lg:inset-x-auto lg:right-[4%] lg:bottom-[12%] lg:max-w-[52rem] lg:items-start lg:text-left xl:right-[calc(10%-1vw)]">
        <p className="font-geist text-[2.25rem] leading-[1.4] text-black/50">
          This is a 404 error, which means you&apos;ve clicked on a bad link or entered an invalid
          URL. Maybe what you&apos;re looking for can be found on the homepage.
        </p>
        <div className="flex items-center w-full gap-[0.75rem] lg:w-auto">
          <Link
            href="/"
            className="flex justify-center items-center w-full font-medium text-black capitalize transition-colors font-geist rounded-[1.25rem] bg-[#98d8ff] px-[3.75rem] py-[1.5rem] text-[2rem] tracking-[-0.05em] hover:bg-[#7fceff] lg:w-auto">
            Back to World Class Edge
          </Link>
          {/* Secondary back-arrow is desktop-only; mobile shows a single full-width CTA. */}
          <button
            type="button"
            aria-label="Go back"
            onClick={goBack}
            className="hidden justify-center items-center text-black transition-colors rounded-[1.25rem] bg-[#98d8ff] p-[1.5rem] hover:bg-[#7fceff] lg:flex">
            {/* `size-*` is Tailwind 3.4+; this repo is on 3.3.3, so w/h explicitly. */}
            <ArrowUpRight className="w-[3rem] h-[3rem]" />
          </button>
        </div>
      </motion.div>
    </section>
  );
}
